import * as React from 'react'

import type PhotoMarkupStore from './PhotoMarkup.store'
import { IPoint } from './photoMarkupModel'

interface IGestureEvent extends UIEvent {
  scale: number
  clientX: number
  clientY: number
}

const WHEEL_ZOOM_SPEED = 0.01
const WHEEL_LINE_PX = 16

function localPoint(
  element: Element,
  event: { clientX: number; clientY: number },
): IPoint {
  const rect = element.getBoundingClientRect()
  return { x: event.clientX - rect.left, y: event.clientY - rect.top }
}

function midpoint(a: IPoint, b: IPoint): IPoint {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
}

function distance(a: IPoint, b: IPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

export default function usePhotoMarkupZoom(
  store: PhotoMarkupStore,
  canvasRef: React.RefObject<HTMLCanvasElement>,
) {
  const touches = React.useRef(new Map<number, IPoint>()).current
  const isPinching = React.useRef(false)

  React.useEffect(() => {
    const canvas = canvasRef.current
    let gestureScale = 1

    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      const point = localPoint(canvas, event)
      const unit =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE ? WHEEL_LINE_PX : 1
      const dx = event.deltaX * unit
      const dy = event.deltaY * unit
      if (event.ctrlKey) {
        store.zoomView(point, point, Math.exp(-dy * WHEEL_ZOOM_SPEED))
      } else {
        store.zoomView(point, { x: point.x - dx, y: point.y - dy }, 1)
      }
    }

    const onGestureStart = (event: Event) => {
      event.preventDefault()
      gestureScale = 1
    }

    const onGestureChange = (event: Event) => {
      event.preventDefault()
      if (touches.size) return
      const { scale } = event as IGestureEvent
      const point = localPoint(canvas, event as IGestureEvent)
      store.zoomView(point, point, scale / gestureScale)
      gestureScale = scale
    }

    canvas.addEventListener('wheel', onWheel, { passive: false })
    canvas.addEventListener('gesturestart', onGestureStart)
    canvas.addEventListener('gesturechange', onGestureChange)
    return () => {
      canvas.removeEventListener('wheel', onWheel)
      canvas.removeEventListener('gesturestart', onGestureStart)
      canvas.removeEventListener('gesturechange', onGestureChange)
    }
  }, [store, canvasRef, touches])

  const pointerDown = (event: React.PointerEvent<HTMLElement>): boolean => {
    if (event.pointerType !== 'touch') return false
    touches.set(event.pointerId, localPoint(event.currentTarget, event))
    if (touches.size === 2) {
      store.gestures.abort()
      isPinching.current = true
    }
    if (!isPinching.current) return false
    event.currentTarget.setPointerCapture(event.pointerId)
    return true
  }

  const pointerMove = (event: React.PointerEvent<HTMLElement>): boolean => {
    if (!touches.has(event.pointerId)) return isPinching.current
    const [a, b] = [...touches.values()]
    touches.set(event.pointerId, localPoint(event.currentTarget, event))
    if (!isPinching.current) return false
    const [nextA, nextB] = [...touches.values()]
    if (b && nextB && distance(a, b)) {
      store.zoomView(
        midpoint(a, b),
        midpoint(nextA, nextB),
        distance(nextA, nextB) / distance(a, b),
      )
    }
    return true
  }

  const pointerEnd = (event: React.PointerEvent<HTMLElement>): boolean => {
    touches.delete(event.pointerId)
    if (!isPinching.current) return false
    if (!touches.size) isPinching.current = false
    return true
  }

  return { pointerDown, pointerMove, pointerEnd }
}
